import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useMemo } from 'react'
import flowSlice from './flow-slice'
import { RootState, TypedDispatch } from 'redux/store'
import { FlowStep, FlowSteps } from './flow-types'
import { getAnalyticsInstance } from 'modules/analytics/analytics' // Import the analytics instance
import { DONATION_GRID_STEP_NAME } from './utils/steps/donation-grid'

/**
 * Tracks forward navigation between form steps using analytics.
 * Sends an event with both the current step and the next step.
 *
 * @param toStepName - The name of the step being navigated to.
 * @param fromStepName - The name of the step being navigated from.
 */
export const trackStepForward = (toStepName: string, fromStepName: string) => {
  const analytics = getAnalyticsInstance()

  if (analytics) {
    analytics.track('studio_form_start', {
      to_step: toStepName,
      from_step: fromStepName,
    })
  }
}

/**
 * Tracks backward navigation between form steps using analytics.
 * Sends an event with both the current step and the previous step.
 *
 * @param toStepName - The name of the step being navigated to.
 * @param fromStepName - The name of the step being navigated from.
 */
export const trackStepBackward = (toStepName: string, fromStepName: string) => {
  const analytics = getAnalyticsInstance()

  if (analytics) {
    analytics.track('studio_form_back', {
      to_step: toStepName,
      from_step: fromStepName,
    })
  }
}

const useFlow = () => {
  const dispatch = useDispatch<TypedDispatch<RootState>>()
  const { currentStepName, stepsMap } = useSelector((state: RootState) => state.flow)
  const urlParams = useSelector((state: RootState) => state.urlParams)
  const currentStep = stepsMap[currentStepName]

  const sortedAndEnabledSteps = useSortedAndEnabledSteps(stepsMap)

  const nextStep = useNextStep(sortedAndEnabledSteps, currentStep)
  const nextGroupOrStep = useNextStep(sortedAndEnabledSteps, currentStep, true)
  const prevStep = usePrevStep(sortedAndEnabledSteps, currentStep)

  /**
   * Important: using the native history API instead of NextJS router API because
   * NextJS router API causes both pages/_app and the current page to re-render,
   * re-initializing the Redux store.
   */
  const syncToBrowserHistory = useCallback(
    (stepName: string) => {
      const toStep = stepsMap[stepName]

      // Navigating to the previous step
      if (prevStep && prevStep.index === toStep.index) {
        history.back()
      }

      // Navigating to the next step
      if (nextStep && nextStep.index === toStep.index) {
        const url = new URL(location.href)

        url.searchParams.set('step', stepName)

        history.pushState({}, '', url)
      }
    },
    [stepsMap, nextStep, prevStep],
  )

  const goToStep = useCallback(
    (stepName: string, shouldSyncToBrowserHistory = true) => {
      if (shouldSyncToBrowserHistory) {
        syncToBrowserHistory(stepName)
      }

      trackStepForward(stepName, currentStepName)

      dispatch(flowSlice.actions.goToStep(stepName))
    },
    [currentStepName, dispatch, syncToBrowserHistory],
  )

  // Handle history popstate
  useEffect(() => {
    const onPopstate = () => {
      const urlParams = new URLSearchParams(location.href)

      const stepURLParam = urlParams.get('step')

      if (!stepURLParam) return

      const foundStep = stepsMap[stepURLParam]

      /**
       * Only allow navigating to the previous step from the browser.
       *
       * We don't want to allow the donor to navigate to the next step from the browser
       * because we don't know if the current step is valid.
       */
      if (foundStep && prevStep && prevStep.index === foundStep.index) {
        goToStep(prevStep.name, false)
      }
    }

    window.addEventListener('popstate', onPopstate)

    return () => {
      window.removeEventListener('popstate', onPopstate)
    }
  }, [goToStep, stepsMap, currentStep, prevStep])

  /**
   * Keep the browser history in sync with initial step name so that donor can
   * navigate back when they don't start on the donation step
   */
  useEffect(() => {
    if (urlParams.frequency && urlParams.amount) {
      const url = new URL(location.href)
      url.searchParams.set('step', DONATION_GRID_STEP_NAME)
      history.pushState({}, '', url)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // empty so it runs only once

  return useMemo(
    () => ({
      goToStep,
      nextStep,
      prevStep,
      nextGroupOrStep,
      currentStep,
    }),
    [goToStep, nextStep, prevStep, nextGroupOrStep, currentStep],
  )
}

export default useFlow

const useNextStep = (
  enabledStepsArr: FlowStep[],
  currentStep: FlowStep,
  groupSteps?: boolean,
): FlowStep | undefined =>
  useMemo(
    () =>
      enabledStepsArr.find((step) => {
        if (step.index <= currentStep.index) return false
        if (step.maxVisits && step.numVisits >= step.maxVisits) return false
        if (groupSteps && currentStep.group && step.group === currentStep.group) return false
        return true
      }),
    [enabledStepsArr, currentStep, groupSteps],
  )

const usePrevStep = (enabledStepsArr: FlowStep[], currentStep: FlowStep): FlowStep | undefined =>
  useMemo(
    () =>
      enabledStepsArr.reduce((prevStep, step) => {
        if (step.index >= currentStep.index) return prevStep
        if (step.maxVisits && step.numVisits >= step.maxVisits) return prevStep

        const thisStepIsGrouped = Boolean(step?.group)
        const thePrevStepIsInTheSameGroup = prevStep?.group && prevStep.group === step.group
        const theCurrentStepIsInTheSameGroup = step.group === currentStep.group

        if (thisStepIsGrouped && thePrevStepIsInTheSameGroup) {
          if (theCurrentStepIsInTheSameGroup) return step
          return prevStep
        }

        return step
      }, undefined as FlowStep | undefined),
    [enabledStepsArr, currentStep],
  )

const stepsByIndex = (a: FlowStep, b: FlowStep) => {
  if (a.index === undefined || b.index === undefined) return 0
  return a.index < b.index ? -1 : 1
}

// Utility fn to turn the app flow steps map into a sorted array
const useSortedAndEnabledSteps = (steps: FlowSteps): FlowStep[] =>
  useMemo(() => {
    const stepsArray = Object.values(steps).filter((step) => step.enabled)

    stepsArray.sort(stepsByIndex)

    return stepsArray
  }, [steps])
